import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, IAppConfig } from '@tcc-mono/shared/app-config';
import { BaseService, Response } from '@tcc-mono/shared/data-access';
import { Observable } from 'rxjs';

export interface DashboardDateRange {
  start_date: string;
  end_date: string;
}

export interface RideAnimalsBySource {
  kvs: number;
  trader: number;
  external: number;
  total: number;
}

export interface KvsErrorDetail {
  id: number;
  order_number?: string;
  relation_name?: string;
  identification_number?: string;
  country_code?: string;
  ride_number?: string;
  ride_id?: number;
  ride_source?: string;
  error_code?: string;
  error_message?: string;
  kvs_error?: string[];
  kvs_closed_at?: string | null;
  retryable?: boolean;
  created_at: string;
}

export interface KvsReceiveRidesRetryResponse {
  success: boolean;
  purchase_order_id?: number | null;
}

export interface TransportGroupVc2vcErrorDetail {
  id: number;
  hub_name: string;
  destination_hub_name: string;
  date: string;
  kvs_error: string;
  created_at: string;
}

export interface TransportDepartureErrorDetail {
  id: number;
  license_plate: string;
  destination_ubn: string;
  departure_date_time_utc: string;
  kvs_departure_message_status: string;
  created_at: string;
  stable_id?: number;
}

export interface UnreportedTransport {
  id: number;
  hub_name: string;
  destination_name: string;
  destination_relation_name: string;
  date: string;
  kvs_vc2vc_closed_at: string | null;
  created_at: string;
}

export interface UnreportedVc2vcTransport {
  id: number;
  hub_name: string;
  destination_hub_name: string;
  date: string;
  created_at: string;
}


export interface TransportGroupAnimalsCount {
  destination_hub: number;
  destination_stable: number;
  animals_to_place: number;
  total: number;
}

export interface PurchaseOrderLinesCount {
  kvs: number;
  trader: number;
  total: number;
  without_purchase_order: {
    kvs: number;
    trader: number;
    total: number;
  };
}

export interface KvsReceivedOrders {
  total_orders: number;
  received_without_error: number;
  received_with_error: number;
  not_received: number;
  not_closed: number;
}

export interface UnreportedRide {
  id: number;
  ride_number: string;
  created_at: string;
  relation_name: string;
}

export interface KvsUnreportedTraderRides {
  count: number;
  rides: UnreportedRide[];
}

export interface TransportKvsStatus {
  total: number;
  reported: number;
  unreported: number;
}

export interface TransportDepartureErrorDetails {
  critical: TransportDepartureErrorDetail[];
  non_participant: TransportDepartureErrorDetail[];
}

export interface KvsErrorsOverview {
  transport_group_vc2vc_errors: number;
  transport_group_vc2vc_error_details: TransportGroupVc2vcErrorDetail[];
  transport_departure_errors: number;
  transport_departure_error_details: TransportDepartureErrorDetails;
  transport_departure_critical_errors: number;
  transport_departure_non_participant_errors: number;
  purchase_order_errors: number;
  purchase_order_error_details: KvsErrorDetail[];
  ride_animal_errors: number;
  ride_animal_error_details: KvsErrorDetail[];
  total_errors: number;
}

export interface WeeklyStats {
  ride_animals_by_source: RideAnimalsBySource;
  purchase_order_lines_count: PurchaseOrderLinesCount;
  transport_group_animals_count: TransportGroupAnimalsCount;
  kvs_received_orders: KvsReceivedOrders;
  kvs_unreported_trader_rides: KvsUnreportedTraderRides;
  kvs_errors_overview: KvsErrorsOverview;
}

export interface UnchangedPurchaseOrderLine {
  id: number;
  animal_id: number;
  animal_country_code: string;
  animal_identification_number: string;
  created_at: string;
  purchase_order: {
    id: number;
    order_number: string;
    relation_name: string;
  };
}

export interface UnclosedPurchaseOrder {
  id: number;
  order_number: string;
  created_at: string;
  relation_name: string;
}

export interface AttentionPoints {
  unchanged_purchase_order_lines: {
    count: number;
    lines: UnchangedPurchaseOrderLine[];
  };
  unclosed_purchase_orders: {
    count: number;
    orders: UnclosedPurchaseOrder[];
  };
  unreported_transports: {
    count: number;
    transports: UnreportedTransport[];
  };
  unreported_vc2vc_transports: {
    count: number;
    transports: UnreportedVc2vcTransport[];
  };
}

export interface QueueStatus {
  status: string;
  worker_status: string;
  message: string;
  reserved_jobs_count: number;
  failed_jobs_count: number;
  recent_failed_jobs: number;
  pending_jobs_count: number;
  delayed_jobs_count: number;
  queue_driver: string;
  last_checked: string;
  error?: string;
}

export interface ApiStatus {
  status: string;
  response_time_ms: number;
  last_checked: string;
  error: string | null;
}

export interface KvsDashboardStatus {
  status: string;
  response_time_ms: number;
  last_checked: string;
  error: string | null;
}

export interface RvoDashboardStatus {
  status: string;
  response_time_ms: number;
  last_checked: string;
  error: string | null;
  tested_animal: string;
}


export interface RideAnimalWithoutPurchaseOrder {
  id: number;
  identification_number: string;
  country_code: string;
  work_number: string;
  ride_id: number;
  ride_number: string;
  ride_origin: string;
  relation_name: string;
  gender?: string;
  color?: string;
  created_at: string;
}

export interface RideAnimalsWithoutPurchaseOrdersResponse {
  date_range: {
    start_date: string;
    end_date: string;
  };
  count: number;
  ride_animals: RideAnimalWithoutPurchaseOrder[];
}

export interface DashboardData {
  date_range: DashboardDateRange;
  weekly_stats: WeeklyStats;
  attention_points: AttentionPoints;
  queue_status: QueueStatus;
}

// Dashboard Charts V2 Interfaces
export interface ChartData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    borderColor?: string;
    backgroundColor?: string | string[];
    type?: 'line' | 'bar';
    y_axis_id?: 'left' | 'right';
    stack?: string;
  }>;
  meta?: {
    visualization?: {
      preferred_mode?: string;
      left_axis_label?: string;
      right_axis_label?: string;
      description?: string;
      [key: string]: any;
    };
    [key: string]: any;
  };
}

export interface DashboardChartsKpis {
  total_animals: number;
  total_orders: number;
  success_rate: number;
  error_count: number;
  animals_to_place: number;
  ride_animals_without_purchase_orders?: number;
  ride_animals_without_purchase_orders_prev?: number;
  ride_animals_without_purchase_orders_delta_pct?: number | null;
  purchased_animals?: number;
  purchased_animals_prev?: number;
  purchased_animals_delta_pct?: number | null;
  placed_animals?: number;
  placed_animals_prev?: number;
  placed_animals_delta_pct?: number | null;
  total_animals_prev?: number;
  total_animals_delta_pct?: number | null;
  total_orders_prev?: number;
  total_orders_delta_pct?: number | null;
  success_rate_prev?: number;
  success_rate_delta_pct?: number | null;
  error_count_prev?: number;
  error_count_delta_pct?: number | null;
  animals_to_place_prev?: number;
  animals_to_place_delta_pct?: number | null;
}

export interface DashboardChartsActionItemRide {
  id: number;
  number: string;
  animal_count: number;
  created_at: string;
  relation_name: string;
}

export interface DashboardChartsActionItemOrder {
  id: number;
  order_number: string;
  relation_name: string;
  created_at: string;
}

export interface DashboardChartsActionItemTransport {
  id: number;
  date: string;
  hub_name: string;
  destination_name: string;
  created_at: string;
  transport_group_id?: number; // Optioneel: nodig voor klikbare navigatie naar transport groep
}

export interface DashboardChartsActionItems {
  unreported_rides: {
    count: number;
    rides: DashboardChartsActionItemRide[];
  };
  unclosed_orders: {
    count: number;
    orders: DashboardChartsActionItemOrder[];
  };
  unreported_transports: {
    count: number;
    transports: DashboardChartsActionItemTransport[];
  };
  unreported_vc2vc: {
    count: number;
    transports: DashboardChartsActionItemTransport[];
  };
  animals_to_place: {
    count: number;
  };
}

export interface DashboardChartsRecentRide {
  id: number;
  number: string;
  origin: 'kvs' | 'trader';
  animal_count: number;
  relation_name: string;
  created_at: string;
}

export interface DashboardChartsRecentOrder {
  id: number;
  order_number: string;
  relation_name: string;
  closed: boolean;
  created_at: string;
}

export interface DashboardChartsRecentTransport {
  id: number;
  date: string;
  hub_name: string;
  destination_name: string;
  created_at: string;
  transport_group_id?: number; // Optioneel: nodig voor klikbare navigatie naar transport groep
}

export interface DashboardChartsRecentActivity {
  recent_rides: DashboardChartsRecentRide[];
  recent_orders: DashboardChartsRecentOrder[];
  recent_transports: DashboardChartsRecentTransport[];
}

export interface DashboardChartsData {
  period: 'week' | 'month' | 'quarter' | 'year';
  date_range: {
    from: string;
    to: string;
  };
  kpis: DashboardChartsKpis;
  charts: {
    animals_over_time: ChartData;
    animals_by_source: ChartData;
    orders_status: ChartData;
    transport_status: ChartData;
    errors_by_type: ChartData;
  };
  action_items: DashboardChartsActionItems;
  recent_activity: DashboardChartsRecentActivity;
}

export interface TopRelation {
  relation_id: number;
  relation_name: string;
  order_count: number;
  kvs_error_count: number;
  kvs_warning_count?: number;
  kvs_animal_error_count?: number;
  success_rate_pct: number;
  status: 'ok' | 'let_op' | 'fouten';
}

export interface SystemStatus {
  kvs: { status: string; [key: string]: any };
  rvo: { status: string; [key: string]: any };
  queue: { [key: string]: any };
}

export interface PurchasedAnimalsByColorRow {
  color_id: number | null;
  color_code: string;
  color_name: string;
  gender_id: number | null;
  gender_code: string;
  gender_name: string;
  animal_count: number;
}

// Dashboard Report response (V2)
export interface DashboardReportData {
  period: 'week' | 'month' | 'quarter' | 'year';
  tab: 'all' | 'ritten' | 'inkoop' | 'transport' | 'leveranciers' | 'facturatie';
  date_range: { from: string; to: string };
  kpis: DashboardChartsKpis;
  charts?: {
    animals_over_time?: ChartData;
    animals_by_source?: ChartData;
    orders_status?: ChartData;
    transport_status?: ChartData & {
      deadline_warning?: number;
      deadline_exceeded?: number;
      deadline_warning_details?: any[];
      deadline_exceeded_details?: any[];
    };
    errors_by_type?: ChartData;
    rides_count_over_time?: ChartData;
    ride_animals_count_over_time?: ChartData;
    animals_wf_over_time?: ChartData;
    average_purchase_weight_over_time?: ChartData;
  };
  tables?: {
    purchased_animals_by_color?: PurchasedAnimalsByColorRow[];
    [key: string]: any;
  };
  action_items?: {
    overview?: any;
    ritten?: any;
    inkoop?: any;
    transport?: any;
    leveranciers?: any;
    facturatie?: any;
    [key: string]: any;
  };
  top_relations?: TopRelation[];
  system_status?: SystemStatus;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService extends BaseService<DashboardData> {
  public constructor(
    _http: HttpClient,
    @Inject(APP_CONFIG) appConfig: IAppConfig
  ) {
    super(_http, appConfig);
    this.path = 'api/dashboard';
  }

  public getDashboardData(startDate: string, endDate: string): Observable<Response<DashboardData>> {
    const params = {
      start_date: startDate,
      end_date: endDate
    };
    return this._http.get<Response<DashboardData>>(this.getUrl(this.path), { params: Object(params) });
  }

  public getRideAnimalsWithoutPurchaseOrders(startDate?: string, endDate?: string): Observable<Response<RideAnimalsWithoutPurchaseOrdersResponse>> {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    return this._http.get<Response<RideAnimalsWithoutPurchaseOrdersResponse>>(
      this.getUrl(`${this.path}/ride-animals-without-purchase-orders`),
      { params: Object(params) }
    );
  }

  public getKvsStatus(): Observable<KvsDashboardStatus> {
    return this._http.get<KvsDashboardStatus>(this.getUrl(`${this.path}/kvs-status`));
  }

  public getRvoStatus(): Observable<RvoDashboardStatus> {
    return this._http.get<RvoDashboardStatus>(this.getUrl(`${this.path}/rvo-status`));
  }

  public getCharts(
    period: 'week' | 'month' | 'quarter' | 'year' = 'week',
    startDate?: string,
    endDate?: string
  ): Observable<Response<DashboardChartsData>> {
    const params: any = {};

    // Als startDate en endDate zijn opgegeven, gebruik die (hebben prioriteit)
    if (startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    } else {
      params.period = period;
    }

    return this._http.get<Response<DashboardChartsData>>(this.getUrl(`${this.path}/charts`), { params: Object(params) });
  }

  public getDashboardReport(params: {
    period?: 'week' | 'month' | 'quarter' | 'year';
    tab?: 'all' | 'ritten' | 'inkoop' | 'transport' | 'leveranciers' | 'facturatie';
    start_date?: string;
    end_date?: string;
  }): Observable<{ success: boolean; data: DashboardReportData }> {
    return this._http.get<{ success: boolean; data: DashboardReportData }>(
      this.getUrl(`${this.path}/report`),
      { params: Object(params) }
    );
  }
}
